import { useState, useEffect } from 'react';
import { AdminLayout } from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import {
  Calendar, Plus, X, Clock, MapPin, User, Phone, Mail,
  CheckCircle2, XCircle, AlertCircle, ExternalLink, Trash2, Edit2
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CALENDLY_URL = "https://calendly.com/seantherealtor/15min";

const APT_TYPES = [
  { id: 'showing', name: 'Showing' },
  { id: 'consultation', name: 'Consultation' },
  { id: 'listing_appointment', name: 'Listing Appointment' },
  { id: 'closing', name: 'Closing' },
  { id: 'inspection', name: 'Inspection' },
  { id: 'appraisal', name: 'Appraisal' },
  { id: 'follow_up', name: 'Follow-Up' },
  { id: 'other', name: 'Other' },
];

const STATUS_STYLES = {
  scheduled: 'bg-blue-500/20 text-blue-400',
  confirmed: 'bg-green-500/20 text-green-400',
  completed: 'bg-emerald-500/20 text-emerald-400',
  cancelled: 'bg-red-500/20 text-red-400',
  no_show: 'bg-yellow-500/20 text-yellow-400',
};

const STATUS_ICONS = {
  scheduled: Clock,
  confirmed: CheckCircle2,
  completed: CheckCircle2,
  cancelled: XCircle,
  no_show: AlertCircle,
};

export const AdminAppointments = () => {
  const [appointments, setAppointments] = useState([]);
  const [stats, setStats] = useState({});
  const [leads, setLeads] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    lead_id: '', agent_id: '', title: '', appointment_date: '',
    appointment_type: 'consultation', location: '', notes: '', calendly_event_url: ''
  });

  useEffect(() => { fetchData(); }, [statusFilter]);

  const fetchData = async () => {
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const [aptsRes, statsRes, leadsRes, agentsRes] = await Promise.all([
        axios.get(`${API}/appointments${params}`),
        axios.get(`${API}/appointments/stats/summary`),
        axios.get(`${API}/leads`),
        axios.get(`${API}/agents`)
      ]);
      setAppointments(aptsRes.data);
      setStats(statsRes.data);
      const allLeads = Array.isArray(leadsRes.data) ? leadsRes.data : leadsRes.data.leads || [];
      setLeads(allLeads);
      setAgents(agentsRes.data);
    } catch (error) { console.error('Appointments fetch error:', error); } finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!form.title || !form.appointment_date) {
      toast.error('Title and date are required');
      return;
    }
    try {
      if (editingId) {
        await axios.put(`${API}/appointments/${editingId}`, form);
        toast.success('Appointment updated');
      } else {
        await axios.post(`${API}/appointments`, form);
        toast.success('Appointment created');
      }
      setShowCreate(false);
      setEditingId(null);
      setForm({ lead_id: '', agent_id: '', title: '', appointment_date: '', appointment_type: 'consultation', location: '', notes: '', calendly_event_url: '' });
      fetchData();
    } catch { toast.error('Failed to save appointment'); }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axios.put(`${API}/appointments/${id}`, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      fetchData();
    } catch { toast.error('Failed to update status'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this appointment?')) return;
    try {
      await axios.delete(`${API}/appointments/${id}`);
      toast.success('Appointment deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const handleEdit = (apt) => {
    setForm({
      lead_id: apt.lead_id || '',
      agent_id: apt.agent_id || '',
      title: apt.title,
      appointment_date: apt.appointment_date,
      appointment_type: apt.appointment_type || 'consultation',
      location: apt.location || '',
      notes: apt.notes || '',
      calendly_event_url: apt.calendly_event_url || ''
    });
    setEditingId(apt.id);
    setShowCreate(true);
  };

  const formatDate = (d) => {
    if (!d) return '';
    const date = new Date(d);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) +
      ' at ' + date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const isUpcoming = (d) => d && new Date(d) >= new Date();

  return (
    <AdminLayout>
      <div data-testid="admin-appointments">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-light text-[#F5F5F0]" style={{ fontFamily: 'Playfair Display' }}>
              Appointments
            </h1>
            <p className="text-[#808080] mt-1">Schedule and track appointments with leads</p>
          </div>
          <div className="flex gap-2">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="btn-outline-gold" data-testid="open-calendly-btn">
                <ExternalLink className="w-4 h-4 mr-2" /> Open Calendly
              </Button>
            </a>
            <Button onClick={() => { setEditingId(null); setForm({ lead_id: '', agent_id: '', title: '', appointment_date: '', appointment_type: 'consultation', location: '', notes: '', calendly_event_url: '' }); setShowCreate(true); }} className="btn-gold" data-testid="new-appointment-btn">
              <Plus className="w-4 h-4 mr-2" /> New Appointment
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><div className="spinner" /></div>
        ) : (
          <>
            {/* Stats Row */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="appointment-stats">
              {[
                { label: 'Total', value: stats.total || 0, color: '#D4AF37' },
                { label: 'Today', value: stats.today || 0, color: '#3B82F6' },
                { label: 'Upcoming', value: stats.upcoming || 0, color: '#10B981' },
                { label: 'Completed', value: stats.completed || 0, color: '#22C55E' },
                { label: 'Cancelled', value: stats.cancelled || 0, color: '#EF4444' },
                { label: 'No-Show', value: stats.no_show || 0, color: '#F59E0B' },
              ].map(s => (
                <div key={s.label} className="dashboard-card text-center" data-testid={`stat-${s.label.toLowerCase()}`}>
                  <p className="text-2xl font-semibold" style={{ color: s.color }}>{s.value}</p>
                  <p className="text-[#808080] text-xs mt-1">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Status Filter */}
            <div className="flex gap-2 mb-6" data-testid="status-filter">
              {['', 'scheduled', 'confirmed', 'completed', 'cancelled', 'no_show'].map(s => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg capitalize transition-colors ${statusFilter === s ? 'bg-[#D4AF37]/20 text-[#D4AF37]' : 'bg-[#111111] text-[#808080] hover:text-[#F5F5F0]'}`}
                  data-testid={`filter-${s || 'all'}`}
                >
                  {s || 'All'}
                </button>
              ))}
            </div>

            {/* Appointments List */}
            <div className="space-y-3" data-testid="appointments-list">
              {appointments.length === 0 ? (
                <div className="dashboard-card text-center py-12">
                  <Calendar className="w-12 h-12 text-[#D4AF37]/30 mx-auto mb-3" />
                  <p className="text-[#808080]">No appointments yet. Schedule one to get started.</p>
                </div>
              ) : (
                appointments.map(apt => {
                  const StatusIcon = STATUS_ICONS[apt.status] || Clock;
                  const upcoming = isUpcoming(apt.appointment_date);
                  return (
                    <div key={apt.id} className={`dashboard-card flex flex-col sm:flex-row sm:items-center gap-4 ${!upcoming && apt.status === 'scheduled' ? 'opacity-60' : ''}`} data-testid={`appointment-${apt.id}`}>
                      {/* Date Column */}
                      <div className="flex-shrink-0 w-20 text-center">
                        <div className="text-3xl font-bold text-[#D4AF37]">{new Date(apt.appointment_date).getDate()}</div>
                        <div className="text-xs text-[#808080] uppercase">{new Date(apt.appointment_date).toLocaleString('en-US', { month: 'short' })}</div>
                        <div className="text-[10px] text-[#808080]">{new Date(apt.appointment_date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</div>
                      </div>

                      {/* Main Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-[#F5F5F0] font-medium text-sm truncate">{apt.title}</h3>
                          <span className={`px-2 py-0.5 text-[10px] rounded-full capitalize ${STATUS_STYLES[apt.status] || 'bg-[#333] text-[#808080]'}`}>
                            {apt.status?.replace('_', ' ')}
                          </span>
                          <span className="px-2 py-0.5 bg-[#1A1A1A] text-[#808080] text-[10px] rounded capitalize">{apt.appointment_type?.replace('_', ' ')}</span>
                        </div>
                        <div className="flex flex-wrap gap-3 text-xs text-[#808080]">
                          {apt.lead_name && (
                            <span className="flex items-center gap-1"><User className="w-3 h-3 text-[#D4AF37]" /> {apt.lead_name}</span>
                          )}
                          {apt.agent_name && (
                            <span className="flex items-center gap-1"><User className="w-3 h-3 text-[#808080]" /> {apt.agent_name}</span>
                          )}
                          {apt.location && (
                            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {apt.location}</span>
                          )}
                          {apt.lead_phone && (
                            <a href={`tel:${apt.lead_phone}`} className="flex items-center gap-1 text-[#D4AF37]"><Phone className="w-3 h-3" /> {apt.lead_phone}</a>
                          )}
                        </div>
                        {apt.notes && <p className="text-[#808080] text-xs mt-1 truncate">{apt.notes}</p>}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <select
                          value={apt.status}
                          onChange={e => handleStatusChange(apt.id, e.target.value)}
                          className="px-2 py-1.5 bg-[#1A1A1A] border border-[#333] rounded text-[#F5F5F0] text-xs"
                          data-testid={`status-select-${apt.id}`}
                        >
                          <option value="scheduled">Scheduled</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                          <option value="no_show">No-Show</option>
                        </select>
                        <button onClick={() => handleEdit(apt)} className="p-1.5 text-[#808080] hover:text-[#D4AF37] rounded" data-testid={`edit-apt-${apt.id}`}>
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(apt.id)} className="p-1.5 text-red-400 hover:bg-red-400/10 rounded" data-testid={`delete-apt-${apt.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}

        {/* Create/Edit Modal */}
        {showCreate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" data-testid="appointment-modal">
            <div className="bg-[#111111] p-6 rounded-xl max-w-lg w-full mx-4 border border-[#D4AF37]/20 max-h-[85vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl text-[#F5F5F0]" style={{ fontFamily: 'Playfair Display' }}>
                  {editingId ? 'Edit Appointment' : 'New Appointment'}
                </h3>
                <button onClick={() => { setShowCreate(false); setEditingId(null); }} className="text-[#808080] hover:text-[#F5F5F0]">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-[#808080] text-xs uppercase tracking-wider mb-1 block">Title *</label>
                  <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="e.g., Property Showing - 123 Main St" className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]" data-testid="apt-title-input" />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[#808080] text-xs uppercase tracking-wider mb-1 block">Date & Time *</label>
                    <Input type="datetime-local" value={form.appointment_date} onChange={e => setForm({...form, appointment_date: e.target.value})} className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]" data-testid="apt-date-input" />
                  </div>
                  <div>
                    <label className="text-[#808080] text-xs uppercase tracking-wider mb-1 block">Type</label>
                    <select value={form.appointment_type} onChange={e => setForm({...form, appointment_type: e.target.value})} className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#333] rounded-md text-[#F5F5F0] text-sm" data-testid="apt-type-select">
                      {APT_TYPES.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[#808080] text-xs uppercase tracking-wider mb-1 block">Lead</label>
                    <select value={form.lead_id} onChange={e => setForm({...form, lead_id: e.target.value})} className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#333] rounded-md text-[#F5F5F0] text-sm" data-testid="apt-lead-select">
                      <option value="">No lead linked</option>
                      {leads.map(l => <option key={l.id} value={l.id}>{l.name} ({l.email})</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[#808080] text-xs uppercase tracking-wider mb-1 block">Agent</label>
                    <select value={form.agent_id} onChange={e => setForm({...form, agent_id: e.target.value})} className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#333] rounded-md text-[#F5F5F0] text-sm" data-testid="apt-agent-select">
                      <option value="">No agent assigned</option>
                      {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-[#808080] text-xs uppercase tracking-wider mb-1 block">Location</label>
                  <Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} placeholder="Property address or meeting point" className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]" data-testid="apt-location-input" />
                </div>

                <div>
                  <label className="text-[#808080] text-xs uppercase tracking-wider mb-1 block">Calendly Event URL (optional)</label>
                  <Input value={form.calendly_event_url} onChange={e => setForm({...form, calendly_event_url: e.target.value})} placeholder="https://calendly.com/..." className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]" data-testid="apt-calendly-input" />
                </div>

                <div>
                  <label className="text-[#808080] text-xs uppercase tracking-wider mb-1 block">Notes</label>
                  <Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Additional details..." rows={3} className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0] text-sm" data-testid="apt-notes-input" />
                </div>

                <Button onClick={handleCreate} className="btn-gold w-full" data-testid="save-appointment-btn">
                  {editingId ? 'Update Appointment' : 'Create Appointment'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
